import React, { useState, useEffect, useRef } from "react";
import { DateTime } from "luxon";
import Select from "react-select";
import TabBar from "./TabBar";
import DynamicField from "./DynamicField";
import InteractionContextMenu from "../utils/InteractionContextMenu";
import InteractionItem from "./InteractionItem";
import ContextMenuPortal from "./ContextMenuPortal";
import GoogleCalendarTest from "../../google/GoogleCalendar";
import Email from "../../google/Email";
import SearchSelectInput from "../utils/SearchSelectInput"; 
import SalesTab from "./SalesTab";
import "./RightDetails.css";
import api from "../../api";
import { formatDate, toDateTimeLocalValue, fromDateTimeLocalValue } from "../../utils/dateUtils";
import CustomAutocomplete from "./CustomAutocomplete";
import { stateOptions } from "../../layouts/states";
import { FiMapPin, FiBriefcase, FiExternalLink } from "react-icons/fi";
import SearchSelectPopup from "../utils/SearchSelectPopup";
import AssignmentManager from "../people/AssignmentManager";

const PeopleDetails = ({ data, columns = [], onClose, onSave, showToast = (msg, type) => console.log(msg, type), users = [] }) => {
  const [formData, setFormData] = useState(data || {});
  const [linkedinExpanded, setLinkedinExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState("Home");
  const isPeopleParent = true;
  const [interactions, setInteractions] = useState([]);
  const [interactionFilter, setInteractionFilter] = useState([]);
  const [filteredInteractions, setFilteredInteractions] = useState([]);
  const [showInteractionMenu, setShowInteractionMenu] = useState(false);
  const [interactionMenuPosition, setInteractionMenuPosition] = useState({ x: 0, y: 0 });
  
  const [companies, setCompanies] = useState([]);
  const [editingCompany, setEditingCompany] = useState(false);
  const [sourceSuggestions, setSourceSuggestions] = useState([]);
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [phoneSuggestions, setPhoneSuggestions] = useState([]);
  const [secondaryEmailSuggestions, setSecondaryEmailSuggestions] = useState([]);
  const [secondaryPhoneSuggestions, setSecondaryPhoneSuggestions] = useState([]);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [allPeopleData, setAllPeopleData] = useState([]); // Store full person data for tooltips

  useEffect(() => {
    setFormData(data || {});
    if (data && data.id) {
      fetchInteractions();
    }
  }, [data]);

  useEffect(() => {
    if (interactionFilter.length === 0) {
      setFilteredInteractions(interactions);
    } else {
      const filterValues = interactionFilter.map((opt) => opt.value);
      setFilteredInteractions(
        interactions.filter((i) => filterValues.includes(i.interaction_type))
      );
    }
  }, [interactions, interactionFilter]);

  const fetchInteractions = async () => {
    if (data && data.id) {
      try {
        const response = await api.get(`/interactions/${data.id}`);
        // Get additional rep info for each interaction
        const interactionsWithRepInfo = await Promise.all(
          response.data.map(async (interaction) => {
            if (interaction.added_by) {
              try {
                // Fetch rep info if not included in interaction data
                if (!interaction.rep_name) {
                  const userResponse = await api.get(`/users/${interaction.added_by}`);
                  if (userResponse.data) {
                    const user = userResponse.data;
                    return {
                      ...interaction,
                      rep_name: `${user.first_name || ''} ${user.last_name || ''}`.trim()
                    };
                  }
                }
              } catch (error) {
                console.error("Error fetching rep info:", error);
              }
            }
            return interaction;
          })
        );
        setInteractions(interactionsWithRepInfo);
      } catch (error) {
        console.error("Error fetching interactions:", error);
      }
    }
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await api.get("/companies");
        setCompanies(response.data);
        
        // If we have a company name but no matching company object, refresh formData
        if (formData.company && response.data.length > 0) {
          const companyExists = response.data.some(
            company => company.name?.toLowerCase() === formData.company?.toLowerCase()
          );
          
          // If company doesn't exist in our list but we have a name, ensure it's properly formatted
          if (!companyExists && formData.company) {
            console.log("Company name exists but no matching object found:", formData.company);
          }
        }
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };
    fetchCompanies();
  }, [formData.company]);

  useEffect(() => {
    const fetchAutocompleteSuggestions = async () => {
      try {
        // Fetch ALL people for duplicate detection
        const response = await api.get('/people/autocomplete/all');
        
        // Store full person data for tooltip display
        setAllPeopleData(response.data);
        
        // Get unique name values
        const names = response.data
          .map(person => person.name)
          .filter(name => name && name.trim() !== '')
          .filter((value, index, self) => self.indexOf(value) === index);
        setNameSuggestions(names);
        
        // Get unique source values
        const sources = response.data
          .map(person => person.source)
          .filter(source => source && source.trim() !== '')
          .filter((value, index, self) => self.indexOf(value) === index);
        setSourceSuggestions(sources);
        
        // Get unique email values
        const emails = response.data
          .map(person => person.email)
          .filter(email => email && email.trim() !== '')
          .filter((value, index, self) => self.indexOf(value) === index);
        setEmailSuggestions(emails);
        
        // Get unique phone values
        const phones = response.data
          .map(person => person.phone)
          .filter(phone => phone && phone.trim() !== '')
          .filter((value, index, self) => self.indexOf(value) === index);
        setPhoneSuggestions(phones);
        
        // Get unique secondary email values
        const secondaryEmails = response.data
          .map(person => person.secondary_email)
          .filter(email => email && email.trim() !== '')
          .filter((value, index, self) => self.indexOf(value) === index);
        setSecondaryEmailSuggestions(secondaryEmails);
        
        // Get unique secondary phone values
        const secondaryPhones = response.data
          .map(person => person.secondary_phone)
          .filter(phone => phone && phone.trim() !== '')
          .filter((value, index, self) => self.indexOf(value) === index);
        setSecondaryPhoneSuggestions(secondaryPhones);
        
      } catch (error) {
        console.error('Error fetching autocomplete suggestions:', error);
      }
    };
    fetchAutocompleteSuggestions();
  }, []);

  const handleImmediateChange = (key, value) => {
    const newData = { ...formData, [key]: value };
    setFormData(newData);
    if (onSave) onSave(newData);
  };

  const handleChange = (key, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [key]: value };
      
      // If status is changing to "Closed Won", prepare to save changes first
      if (key === "status" && value === "Closed Won" && prev.status !== "Closed Won") {
        // We need to save the changes first, which will trigger the backend to create a sale
        if (onSave) {
          onSave(updated).then(() => {
            // Close this details panel - the main Page component will handle opening SalesDetails
            if (typeof onClose === 'function') {
              onClose();
            }
          });
        }
      }
      
      return updated;
    });
  };

  const handleBlur = (key) => {
    if (onSave) onSave(formData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (onSave) await onSave(formData);
  };

  const createdAt = data?.created_at ? formatDate(data.created_at) : null;
  const updatedAt = data?.updated_at ? formatDate(data.updated_at) : null;

  const displayColumns = columns.filter((col) => col.accessor !== "selection");

  const statusColumn = displayColumns.find((col) => col.accessor === "status");
  const assignedColumn = displayColumns.find((col) => col.accessor === "assigned_to");

  const mainFields = displayColumns.filter(
    (col) =>
      col.accessor !== "status" &&
      !["linkedin", "linkedin_bio", "more_linkedin_info", "name", "vip", "company", "assigned_to"].includes(col.accessor)
  );

  // Render a field based on its type.
  const renderField = (fieldName) => {
    const col = displayColumns.find((c) => c.accessor === fieldName);
    if (!col) return null;

    // Special handling for state field - dropdown with state options
    if (fieldName === "state") {
      return (
        <select
          value={formData.state || ""}
          onChange={(e) => handleChange("state", e.target.value)}
          onBlur={() => handleBlur("state")}
          className="state-dropdown"
        >
          <option value="">Select State</option>
          {stateOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    // Special handling for autocomplete fields
    if (fieldName === "name") {
      return (
        <CustomAutocomplete
          value={formData.name || ""}
          onChange={(value) => handleChange("name", value)}
          onBlur={() => handleBlur("name")}
          suggestions={nameSuggestions}
          peopleData={allPeopleData}
          fieldType="name"
          currentValue={formData.name}
        />
      );
    }

    if (fieldName === "source") {
      return (
        <CustomAutocomplete
          value={formData.source || ""}
          onChange={(value) => handleChange("source", value)}
          onBlur={() => handleBlur("source")}
          suggestions={sourceSuggestions}
        />
      );
    }

    if (fieldName === "email") {
      return (
        <CustomAutocomplete
          value={formData.email || ""}
          onChange={(value) => handleChange("email", value)}
          onBlur={() => handleBlur("email")}
          suggestions={emailSuggestions}
          peopleData={allPeopleData}
          fieldType="email"
          currentValue={formData.email}
        />
      );
    }

    if (fieldName === "phone") {
      return (
        <CustomAutocomplete
          value={formData.phone || ""}
          onChange={(value) => handleChange("phone", value)}
          onBlur={() => handleBlur("phone")}
          suggestions={phoneSuggestions}
          peopleData={allPeopleData}
          fieldType="phone"
          currentValue={formData.phone}
        />
      );
    }

    if (fieldName === "secondary_email") {
      return (
        <CustomAutocomplete
          value={formData.secondary_email || ""}
          onChange={(value) => handleChange("secondary_email", value)}
          onBlur={() => handleBlur("secondary_email")}
          suggestions={secondaryEmailSuggestions}
        />
      );
    }

    if (fieldName === "secondary_phone") {
      return (
        <CustomAutocomplete
          value={formData.secondary_phone || ""}
          onChange={(value) => handleChange("secondary_phone", value)}
          onBlur={() => handleBlur("secondary_phone")}
          suggestions={secondaryPhoneSuggestions}
        />
      );
    }

    if (fieldName === "last_contacted" || fieldName === "follow_up_date") {
      // Get datetime-local input value with proper timezone handling
      const localValue = formData[fieldName] ? toDateTimeLocalValue(formData[fieldName]) : "";
      
      return (
        <input
          type="datetime-local"
          value={localValue}
          onChange={(e) => {
            // Convert from local datetime input to UTC for storage
            const isoDate = fromDateTimeLocalValue(e.target.value);
            handleChange(fieldName, isoDate);
          }}
          onBlur={() => handleBlur(fieldName)}
          className="date-input"
        />
      );
    }

    if (col.accessor === "assigned_to") {
      return (
        <DynamicField
          column={col}
          value={formData[col.accessor]}
          onChange={(value) => handleImmediateChange(col.accessor, value)}
        />
      );
    }

    if (col.DropdownOptions) {
      return (
        <DynamicField
          column={col}
          value={formData[col.accessor]}
          onChange={(value) => handleImmediateChange(col.accessor, value)}
        />
      );
    }

    return (
      <input
        type="text"
        value={formData[col.accessor] || ""}
        onChange={(e) => handleChange(col.accessor, e.target.value)}
        onBlur={() => handleBlur(col.accessor)}
      />
    );
  };

  const interactionOptions = Array.from(
    new Set(interactions.map((i) => i.interaction_type))
  ).map((type) => ({ value: type, label: type }));

  // Add a function to handle opening company details
  const handleOpenCompanyDetails = async (companyName) => {
    if (!companyName) return;
    
    try {
      // Find the company object that matches the name
      const companyObject = companies.find(
        company => company.name?.toLowerCase() === companyName?.toLowerCase()
      );
      
      if (companyObject) {
        // Use a special property to indicate we want to open company details
        const companyWrapper = {
          ...companyObject,
          __isCompanyDetails: true,  // Special flag to indicate this is a company
          fromPage: "Company"
        };
        
        // Close the current details panel and open company details
        if (onClose) {
          onClose();
          // We need a small delay before opening the new details to allow the current one to close
          setTimeout(() => {
            if (typeof window !== 'undefined') {
              // Dispatch a custom event to open company details
              const event = new CustomEvent('openCompanyDetails', { 
                detail: { company: companyWrapper } 
              });
              window.dispatchEvent(event);
            }
          }, 100);
        }
      } else {
        showToast("Company not found", "error");
      }
    } catch (error) {
      console.error("Error opening company details:", error);
      showToast("Error opening company details", "error");
    }
  };

  // Make sure we have a proper reference to the current company
  const getCurrentCompanyObject = () => {
    if (!formData.company) return null;
    if (!companies || companies.length === 0) return null;
    
    // Try to find the company by name match, normalizing both strings
    const normalizedCompanyName = formData.company.toLowerCase().trim();
    
    // Try exact match first
    const exactMatch = companies.find(
      company => company.name?.toLowerCase().trim() === normalizedCompanyName
    );
    
    if (exactMatch) return exactMatch;
    
    // If no exact match, try a more flexible approach
    // looking for companies that contain our company name or vice versa
    const partialMatch = companies.find(company => {
      if (!company.name) return false;
      const normalizedName = company.name.toLowerCase().trim();
      return normalizedName.includes(normalizedCompanyName) || 
             normalizedCompanyName.includes(normalizedName);
    });
    
    return partialMatch || null;
  };

  return (
    <div className="right-details-container">
      <div className="right-details-topbar">
        <span className="page-chip">People</span>
        <button className="close-button" onClick={(e) => { 
          console.log('[PeopleDetails] Close button clicked');
          e.stopPropagation(); 
          console.log('[PeopleDetails] Event propagation stopped, calling onClose');
          onClose(); 
          console.log('[PeopleDetails] onClose called');
        }}>
          ×
        </button>
      </div>

      <TabBar>
        {["Home", "Interactions", "Calendar", "Emails", "Sales"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`tab ${tab === activeTab ? "active" : ""}`}
          >
            {tab}
          </button>
        ))}
      </TabBar>

      <div className="right-details-header">
        <div className="header-top" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 className="person-name" style={{ margin: 0 }}>
            {data?.name || "Unnamed"}
          </h2>
          <label className="vip-checkbox" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <input
              type="checkbox"
              checked={!!formData.vip}
              onChange={(e) => handleImmediateChange("vip", e.target.checked)}
            />
            VIP
          </label>
        </div>
        <div className="date-info">
          {createdAt && <div className="created-at">Added {createdAt}</div>}
          {updatedAt && <div className="updated-at">Last updated {updatedAt}</div>}
        </div>
      </div>
      {activeTab === "Home" && (
        <>
          {(statusColumn || assignedColumn) && (
            <div className="status-center-row" style={{ display: "flex", gap: "20px", justifyContent: "center", alignItems: "center" }}>
              {statusColumn && (
                <DynamicField
                  column={statusColumn}
                  value={formData[statusColumn.accessor]}
                  onChange={(val) => handleChange(statusColumn.accessor, val)}
                />
              )}
              {assignedColumn && (
                <DynamicField
                  column={assignedColumn}
                  value={formData[assignedColumn.accessor]}
                  onChange={(val) => handleImmediateChange(assignedColumn.accessor, val)}
                />
              )}
            </div>
          )}

          <form className="right-details-form" onSubmit={handleSubmit}>
            <div className="fields-section">
              {mainFields.map((col) => (
                <div key={col.accessor} className="right-details-field">
                  <div className="field-label">
                    <label htmlFor={col.accessor}>{col.Header || col.accessor}</label>
                  </div>
                  <div className="field-value">{renderField(col.accessor)}</div>
                </div>
              ))}

              {displayColumns.some((col) => col.accessor === "linkedin") && (
                <>
                  <div className="right-details-field">
                    <div className="field-label">
                      <label htmlFor="linkedin">LinkedIn</label>
                    </div>
                    <div className="field-value" style={{ display: "flex", alignItems: "center" }}>
                      {renderField("linkedin")}
                      <button
                        type="button"
                        className="toggle-btn"
                        onClick={() => setLinkedinExpanded(!linkedinExpanded)}
                        style={{
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          fontSize: "16px",
                          color: "#666",
                          marginLeft: "8px",
                          transform: linkedinExpanded ? "rotate(90deg)" : "rotate(0deg)",
                          transition: "transform 0.2s ease-in-out",
                        }}
                        title={linkedinExpanded ? "Collapse" : "Expand"}
                      >
                        &gt;
                      </button>
                    </div>
                  </div>
                  {linkedinExpanded && (
                    <>
                      <div className="right-details-field sub-field">
                        <div className="field-label">
                          <label htmlFor="linkedin_bio">LinkedIn Bio</label>
                        </div>
                        <div className="field-value">{renderField("linkedin_bio")}</div>
                      </div>
                      <div className="right-details-field sub-field">
                        <div className="field-label">
                          <label htmlFor="more_linkedin_info">More LinkedIn Info</label>
                        </div>
                        <div className="field-value">{renderField("more_linkedin_info")}</div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </form>

          {isPeopleParent && (
            <>
              <div className="company-section">
                <h3>
                  <FiBriefcase style={{ marginRight: "8px" }} />
                  Company
                </h3>
                {editingCompany ? (
                  <div className="company-selector-container">
                    <SearchSelectPopup
                      items={companies}
                      labelKey="name"
                      allowMultiple={false}
                      placeholder="Search companies..."
                      initialSelected={formData.company ? 
                        [getCurrentCompanyObject() || { id: 'temp', name: formData.company }] : 
                        []}
                      useCheckbox={false}
                      onSelect={async (selectedItems) => {
                        if (selectedItems && selectedItems.length > 0) {
                          const company = selectedItems[0];
                          const newData = { ...formData, company: company.name };
                          setFormData(newData);
                          // Ensure the save is completed before closing the popup
                          if (onSave) {
                            try {
                              await onSave(newData);
                              showToast("Company updated successfully", "success");
                            } catch (err) {
                              console.error("Error saving company:", err);
                              showToast("Failed to update company", "error");
                              // Don't close if save failed
                              return;
                            }
                          }
                        } else {
                          const newData = { ...formData, company: "" };
                          setFormData(newData);
                          if (onSave) await onSave(newData);
                        }
                        setEditingCompany(false);
                      }}
                      onClose={() => setEditingCompany(false)}
                    />
                  </div>
                ) : (
                  <div className="company-display">
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                      <p className="company-name">{formData.company || "No company set"}</p>
                      <div className="company-actions">
                        {formData.company && (
                          <button
                            className="view-company-button"
                            onClick={() => handleOpenCompanyDetails(formData.company)}
                            title="View Company Details"
                          >
                            <FiExternalLink />
                          </button>
                        )}
                        <button
                          className="edit-company-button"
                          onClick={() => setEditingCompany(true)}
                          title="Edit Company"
                        >
                          ✏️
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          
          {/* Assignment Manager Section */}
          {data && data.id && (
            <div className="assignments-section" style={{ marginTop: "20px" }}>
              <h3 style={{ marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
                👥 Assignments
              </h3>
              <AssignmentManager
                personId={data.id}
                assignments={data.assignments || { primary: [], secondary: [] }}
                users={users}
                onUpdate={() => {
                  // Refresh the person data to get updated assignments
                  if (onSave) {
                    // Trigger a refresh by calling parent's save handler with current data
                    // This will cause parent to refetch
                  }
                }}
                showToast={showToast}
              />
            </div>
          )}
        </>
      )}

      {activeTab === "Emails" && (
        <>
          {console.log("Passing participantEmail to Email:", data?.email)}
          <Email 
            participantEmail={data?.email} 
            participantName={data?.name}
            showToast={showToast}
            currentUser={data?.assigned_to ? { name: data.assigned_to } : null}
          />
        </>
      )}

      {activeTab === "Calendar" && (
        <div className="calendar-tab">
          <GoogleCalendarTest 
            participantEmail={data?.email} 
            participantName={data?.name} 
            company={data?.company}
          />
        </div>
      )}

      {activeTab === "Sales" && (
        <SalesTab 
          entityId={data?.id} 
          entityType="person" 
          showToast={showToast}
        />
      )}

      {activeTab === "Interactions" && (
        <div className="interactions-container">
          <h3>Interactions History ({filteredInteractions.length})</h3>
          <div style={{ marginBottom: "10px", maxWidth: "300px" }}>
            <Select
              isMulti
              placeholder="Filter by type..."
              options={interactionOptions}
              value={interactionFilter}
              onChange={setInteractionFilter}
            />
          </div>
          <button
            className="add-interaction-button"
            onClick={(e) => {
              const container = document.querySelector('.right-details-container');
              if (container) {
                const rect = container.getBoundingClientRect();
                setInteractionMenuPosition({
                  x: e.clientX - rect.left,
                  y: e.clientY - rect.top,
                });
              }
              setShowInteractionMenu(true);
            }}
          >
            Add Interaction
          </button>

          {showInteractionMenu && (
            <ContextMenuPortal>
              <InteractionContextMenu
                x={interactionMenuPosition.x}
                y={interactionMenuPosition.y}
                rowData={data}
                statusOptions={[]}
                outcomeOptions={["Successful", "Unsuccessful", "Pending"]}
                nextStepOptions={["No Action", "Follow-up Call", "Send Email", "Schedule Meeting"]}
                showToast={showToast}
                onSubmit={async (payload) => {
                  try {
                    await api.post("/interactions", payload);
                    fetchInteractions();
                  } catch (error) {
                    console.error("Error adding interaction:", error);
                  }
                  setShowInteractionMenu(false);
                }}
                onClose={() => setShowInteractionMenu(false)}
              />
            </ContextMenuPortal>
          )}

          {filteredInteractions.length === 0 ? (
            <p>No interactions found.</p>
          ) : (
            <div className="interactions-list">
              {filteredInteractions.map((interaction) => (
                <InteractionItem 
                  key={interaction.id} 
                  interaction={interaction}
                  currentRepId={formData.assigned_to}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PeopleDetails;
